import React from 'react';
import { motion } from 'framer-motion';
interface RatioTableProps {
  headers: string[];
  rows: Array<Array<string | number>>;
  highlightRow?: number;
  highlightCol?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function RatioTable({
  headers,
  rows,
  highlightRow,
  highlightCol,
  label,
  mode = 'support'
}: RatioTableProps) {
  const isStimulus = mode === 'stimulus';
  // Detect multipliers between columns (assuming horizontal table where row[0] is label and row[1...] are numbers)
  // We only do this in support mode
  const multipliers: Array<string | null> = [];
  if (!isStimulus && rows.length > 0) {
    const firstRow = rows[0];
    for (let i = 1; i < firstRow.length - 1; i++) {
      const val1 = Number(firstRow[i]);
      const val2 = Number(firstRow[i + 1]);
      if (!isNaN(val1) && !isNaN(val2) && val1 !== 0) {
        const mult = val2 / val1;
        if (Number.isInteger(mult)) {
          multipliers.push(`×${mult}`);
        } else if (Number.isInteger(val1 / val2)) {
          multipliers.push(`÷${val1 / val2}`);
        } else {
          multipliers.push(null);
        }
      } else {
        multipliers.push(null);
      }
    }
  }
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-id="element-14102" data-mp="2k51b8aqvzyhi">
      
      {label && <div className={`text-center font-semibold text-slate-700 border-b border-slate-100 ${isStimulus ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'}`} data-id="element-14103" data-mp="1resvsqnqjs6m">
        
          {label}
        </div>}
      <div className={`overflow-x-auto ${isStimulus ? 'p-3' : 'p-4'}`} data-id="element-14104" data-mp="21cyu6yac2g6q">
        <table className="w-full border-collapse" data-id="element-14105" data-mp="26730rgfosm7a">
          {/* Top arrows row */}
          {!isStimulus && multipliers.some(m => m !== null) && <thead data-id="element-14106" data-mp="1dp8vdrhmn70h">
              <tr data-id="element-14107" data-mp="1dopg6ijd2wq5">
                <th className="p-1" data-id="element-14108" data-mp="1do7akvat765k"></th>
                {multipliers.map((mult, i) => <th key={`top-arrow-${i}`} colSpan={2} className="p-1 relative h-8" data-id="element-14109" data-mp="15o17y1c8b0n6" data-mpi={i}>
                
                    {mult && <div className="absolute inset-0 flex items-center justify-center -translate-x-1/2 left-full w-full z-10" data-id="element-14110" data-mp="10q82xbmnwcoh">
                        <svg className="w-16 h-6 overflow-visible" viewBox="0 0 100 30" data-id="element-14111" data-mp="1fdt7l9z6qjyi">
                    
                          <path d="M 10 25 Q 50 -5 90 25" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" data-id="element-14112" data-mp="1ag1be3ricboi" />
                    
                          <polygon points="90,25 85,20 85,30" fill="#8b5cf6" data-id="element-14113" data-mp="hr3zqjhsxd4q" />
                        </svg>
                        <div className="absolute -top-2 bg-purple-100 text-purple-800 text-[10px] font-bold px-1.5 py-0.5 rounded border border-purple-200" data-id="element-14114" data-mp="wo4oce4p4ryz">
                          {mult}
                        </div>
                      </div>}
                  </th>)}
                {/* Pad remaining columns if any */}
                {Array.from({
              length: Math.max(0, headers.length - multipliers.length * 2 - 1)
            }).map((_, i) => <th key={`pad-${i}`} className="p-1" data-id="element-14115" data-mp="2apiy9bjlu6lt" data-mpi={i}></th>)}
              </tr>
            </thead>}

          <thead data-id="element-14116" data-mp="2fnu4zdels0zt">
            <tr data-id="element-14117" data-mp="2fk6g4rk3s2rf">
              {headers.map((header, i) => <th key={i} className={`text-center font-bold border-2 ${isStimulus ? 'p-2 text-xs' : 'p-3 text-sm'} ${highlightCol === i ? 'bg-indigo-100 border-indigo-300 text-indigo-900' : 'bg-slate-100 border-slate-300 text-slate-700'}`} data-id="element-14118" data-mp="21nt6g4lt95ni" data-mpi={i}>
                
                  {header}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-14119" data-mp="2yf4z03fmktvx">
            {rows.map((row, rowIndex) => <tr key={rowIndex} className={highlightRow === rowIndex ? 'bg-indigo-50' : 'bg-white'} data-id="element-14120" data-mp="2kmf7xkl8oyry" data-mpi={rowIndex}>
              
                {row.map((cell, cellIndex) => <td key={cellIndex} className={`text-center border-2 border-slate-200 ${isStimulus ? 'p-2 text-xs' : 'p-3 text-sm'} ${highlightRow === rowIndex || highlightCol === cellIndex ? 'font-bold text-indigo-900' : 'text-slate-700'}`} data-id="element-14121" data-mp="2ol4i74aze07d" data-mpi={cellIndex}>
                
                    {cell}
                  </td>)}
              </tr>)}
          </tbody>

          {/* Bottom arrows row */}
          {!isStimulus && multipliers.some(m => m !== null) && <tfoot data-id="element-14122" data-mp="1jghesl4ysyzp">
              <tr data-id="element-14123" data-mp="1ji27e57py7ux">
                <td className="p-1" data-id="element-14124" data-mp="1jhiytf3ykvtk"></td>
                {multipliers.map((mult, i) => <td key={`bottom-arrow-${i}`} colSpan={2} className="p-1 relative h-8" data-id="element-14125" data-mp="1fg74wxe1xofk" data-mpi={i}>
                
                    {mult && <div className="absolute inset-0 flex items-center justify-center -translate-x-1/2 left-full w-full z-10" data-id="element-14126" data-mp="2c4d9xlevyiud">
                        <svg className="w-16 h-6 overflow-visible" viewBox="0 0 100 30" data-id="element-14127" data-mp="278odhqy5xi2a">
                    
                          <path d="M 10 5 Q 50 35 90 5" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" data-id="element-14128" data-mp="1ye9r74f7t79o" />
                    
                          <polygon points="90,5 85,10 85,0" fill="#8b5cf6" data-id="element-14129" data-mp="2ux7m67l4jwuw" />
                        </svg>
                        <div className="absolute top-4 bg-purple-100 text-purple-800 text-[10px] font-bold px-1.5 py-0.5 rounded border border-purple-200" data-id="element-14130" data-mp="2q4o0sog73xfj">
                          {mult}
                        </div>
                      </div>}
                  </td>)}
              </tr>
            </tfoot>}
        </table>
      </div>
    </motion.div>;
}